// audit-safety-integrated
import { validatedFetch, readJson, safeErrorMessage, validNumber } from '../utils/auditSafety';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { Trash2 } from 'lucide-react';
import StoreUpdateForm from './StoreUpdate';
import { Copy } from "lucide-react";

const units = ['Kg', 'Litre', 'Nos', 'Packets', 'Box'];

const categoryMap = {
    Grocery: 'food',
    Housekeeping: 'housekeeping',
    'Veg, Meat, Dairy': 'dailie',
    Stationery: 'stationery',
    Cosmetics: 'cosmetics',
    Others: 'others',
};
const labelMap = {
    food: 'Grocery',
    housekeeping: 'Housekeeping',
    dailie: 'Veg, Meat, Dairy',
    stationery: 'Stationery',
    cosmetics: 'Cosmetics',
    others: 'Others',
};

const Items = () => {
    const location = useLocation();
    const formRef = useRef(null);
    const [allItems, setAllItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState(null);
    const [editedItem, setEditedItem] = useState(null);
    const [editIndex, setEditIndex] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [discountExpiryDate, setDiscountExpiryDate] = useState('');
    const [brandPopup, setBrandPopup] = useState({ open: false, brands: [] });
    const [loadingRow, setLoadingRow] = useState(null);
    const [showStatusIndicators, setShowStatusIndicators] = useState(false);
    const [updatedItems, setUpdatedItems] = useState(new Set());
    const [userRole, setUserRole] = useState('');
    const [inlineEdit, setInlineEdit] = useState({ index: null, field: '', value: '' });
    const [activeEditField, setActiveEditField] = useState(null);
    const [pendingEdits, setPendingEdits] = useState({});
    const [storeUpdateItem, setStoreUpdateItem] = useState(null);
    const [lastUpdatedItem, setLastUpdatedItem] = useState(null);
    const [brandList, setBrandList] = useState([{ brand: "", rate: "", discount: "", packQty: "", packUnit: "Kg" }]);
 const [hasHighlightedRows, setHasHighlightedRows] = useState(false);

const [lastUpdatedMap, setLastUpdatedMap] = useState({});
    const categoryIcons = {
        Grocery: '🍽️',
        Housekeeping: '🧹',
        'Veg, Meat, Dairy': '🥦',
        Stationery: '📎',
        Cosmetics: '💄',
        Others: '📦',
    };

    const isAdmin = userRole === 'rcs-admin';

    const formFields = isAdmin
        ? ['category', 'item', 'unit', 'gst']
        : ['item', 'unit', 'rate', 'discount', 'expiryDate'];

    const tableFields = isAdmin
        ? ['item', 'gst']
        : selectedCategory === 'Veg, Meat, Dairy'
            ? ['item', ...(storeUpdateItem ? [] : ['gst', 'rate'])]
            : ['item', ...(storeUpdateItem ? [] : ['gst', 'brands', 'package', 'rate'])];

    const isLitreItem = (item) => item?.indent?.toLowerCase() === 'litre';
    const isNosItem = (item) => {
        const k = item?.indent?.toLowerCase();
        return k === 'nos' || k === 'packets';
    };

    // ── Fetch items on mount ──────────────────────────────────────────────────
    useEffect(() => {
        const fetchItems = async () => {
            try {

                const response = await validatedFetch(`${API_BASE_URL}/item/all/list`, {
                    headers: {  'Content-Type': 'application/json' },
                });
                const result = await readJson(response);
                if (!result.error && Array.isArray(result.data)) {
                    const allowedCategories = ['food', 'housekeeping', 'dailie', 'stationery', 'cosmetics', 'others'];
                    const filteredData = result.data.filter(item =>
                        allowedCategories.includes(item.category?.toLowerCase())
                    );
setAllItems(filteredData);
                    setCategories(Object.keys(categoryMap));
                    const requestedCategory = location.state?.category;
                    setSelectedCategory(
                        requestedCategory && Object.keys(categoryMap).includes(requestedCategory)
                            ? requestedCategory
                            : 'Grocery'
                    );
                    setUserRole(result.user?.role?.toLowerCase()?.trim());

                    const luRes = await validatedFetch(`${API_BASE_URL}/item/last-updated`, {
                        headers: {  'Content-Type': 'application/json' },
                    });
                    const luResult = await readJson(luRes);
                    if (!luResult.error && luResult.data) {
                        setLastUpdatedMap(luResult.data);
                    }
                }
            } catch (error) {
                void 0;
            }
        };
        fetchItems();
    }, []);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        if (updatedItems.size > 0 || lastUpdatedItem) setShowStatusIndicators(true);
    }, []);

    useEffect(() => {
        const hasGreen = allItems.some(item => updatedItems.has(item.id));
        const hasOrange = lastUpdatedItem !== null;
        if (hasGreen || hasOrange) setHasHighlightedRows(true);
    }, [allItems]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const normalizeCategory = (cat) => {
        const lower = cat?.toLowerCase();
        const found = Object.entries(categoryMap).find(([, value]) => value === lower);
        if (found) return lower;
        return categoryMap[cat] || lower;
    };

    const refreshAllItems = async () => {
        const refreshed = await validatedFetch(`${API_BASE_URL}/item/all/list`, {
            headers: {  'Content-Type': 'application/json' },
        });
const result = await readJson(refreshed);
        if (!result.error && Array.isArray(result.data)) setAllItems(result.data);

        const luRes = await validatedFetch(`${API_BASE_URL}/item/last-updated`, {
            headers: {  'Content-Type': 'application/json' },
        });
        const luResult = await readJson(luRes);
        if (!luResult.error && luResult.data) {
            setLastUpdatedMap(luResult.data);
        }
    };
    const getDisplayedCategoryItems = () => {
        const normalize = (str) => str.replace(/[\s-]/g, '').toLowerCase();
        let filtered = [];
        if (selectedCategory === 'Veg, Meat, Dairy') {
            filtered = allItems.filter(item =>
                item.category?.toLowerCase() === 'dailie' &&
                normalize(item.name).includes(normalize(searchQuery))
            );
        } else {
            const backendCategory = categoryMap[selectedCategory];
            filtered = allItems.filter(item =>
                item.category?.toLowerCase() === backendCategory?.toLowerCase() &&
                normalize(item.name).includes(normalize(searchQuery))
            );
        }
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
    };

    const getPackDisplayLabel = (b, item) => {
        const packValue = Number(b.pack_size) || 0;
        if (isLitreItem(item)) {
            if (packValue === 0) return 'Loose Qty';
            if (packValue < 1) return `${(packValue * 1000).toFixed(0)} ml`;
            return `${packValue} Litre`;
        }
        if (isNosItem(item)) return '1 Pc';
        if (packValue === 0) return 'Loose Qty';
        if (packValue < 1) return `${(packValue * 1000).toFixed(0)} Grams`;
        return `${packValue} ${b.pack_unit || 'Kg'}`;
    };

    const getRateColumnLabel = () => {
        if (storeUpdateItem) return 'Rate';
        const sample = displayedCategoryItems[0];
        if (!sample) return 'Rate';
        if (isLitreItem(sample)) return 'Rate/Ltr';
        if (isNosItem(sample)) return 'Rate/Pc';
        return 'Rate/Kg';
    };

    const displayedCategoryItems = getDisplayedCategoryItems();

    // ── CRUD handlers ─────────────────────────────────────────────────────────
    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        setNewItem(null);
    };

    const handleAddRow = () => {
        const backendCategory = categoryMap[selectedCategory];
        setNewItem({ item: '', category: backendCategory || '', unit: '', gst: '', discount: '', rate: '' });
    };

    const handleSaveRow = async () => {
        if (!newItem?.item?.trim()) return;
        try {

            const payload = {
                category: newItem.category,
                name: newItem.item,
                resident: 'Grams',
                indent: newItem.unit,
                gst: validNumber(newItem.gst || 0, 'GST percentage', { max: 100 }),
            };
            const response = await validatedFetch(`${API_BASE_URL}/item`, {
                method: 'POST',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Item added successfully!');
                setNewItem(null);
                await refreshAllItems();
            } else {
                alert('Failed to add item: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handleUpdateItem = async () => {
        if (!editedItem?.item?.trim()) return;
        try {

            const payload = {
                category: editedItem.category,
                name: editedItem.item,
                resident: 'Grams',
                indent: editedItem.unit,
                gst: validNumber(editedItem.gst || 0, 'GST percentage', { max: 100 }),
            };
            const response = await validatedFetch(`${API_BASE_URL}/item/${editedItem.id}`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Item updated successfully!');
                setNewItem(null);
                setEditedItem(null);
                setEditIndex(null);
                await refreshAllItems();
            } else {
                alert('Failed to update item: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handleDeleteItem = async () => {
        if (!editedItem?.id) return;
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {

            const response = await validatedFetch(`${API_BASE_URL}/item/${editedItem.id}`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'delete' }),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Item deleted successfully!');
                setEditedItem(null);
                setEditIndex(null);
                await refreshAllItems();
            } else {
                alert('Failed to delete item: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handleRateSave = async () => {
        try {
            const payload = {};
            Object.entries(pendingEdits).forEach(([id, rate]) => {
                payload[id] = { rate: validNumber(rate, 'Rate') };
            });
            if (!Object.keys(payload).length) throw new Error('Enter a rate to save.');
            const response = await validatedFetch(`${API_BASE_URL}/item/store/data`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Rates updated successfully!');
                setActiveEditField(null);
                setPendingEdits({});
                window.scrollTo({ top: 0, behavior: 'smooth' });
                await refreshAllItems();
            } else {
                alert('Failed to update rates: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handleDiscountSave = async () => {
        try {
            const payload = {};
            Object.keys(pendingEdits).forEach((key) => {
                if (key.endsWith('_expiry')) return;
                const discount = validNumber(pendingEdits[key], 'Discount percentage', { max: 100 });
                const disc_exp = pendingEdits[`${key}_expiry`] || null;
                if (!disc_exp) throw new Error('Choose an expiry date for each discount.');
                payload[key] = { discount, disc_exp };
            });
            if (!Object.keys(payload).length) throw new Error('Enter a discount and expiry date to save.');
            const response = await validatedFetch(`${API_BASE_URL}/item/store/data`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Discounts updated successfully!');
                setPendingEdits({});
                setActiveEditField(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                await refreshAllItems();
            } else {
                alert('Failed to update discounts: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handlePackageSave = async () => {
        try {
            const payload = {};
            Object.entries(pendingEdits).forEach(([key, value]) => {
                if (key.endsWith('_qty')) {
                    const id = key.replace('_qty', '');
                    const qty = validNumber(value, 'Package quantity');
                    payload[id] = { pack_size: qty, pack_unit: 'Kg' };
                }
            });
            if (!Object.keys(payload).length) throw new Error('Enter a package quantity to save.');
            const response = await validatedFetch(`${API_BASE_URL}/item/store/data`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await readJson(response);
            if (!result.error) {
                setSuccessMessage('Package saved successfully!');
                setPendingEdits({});
                setActiveEditField(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                await refreshAllItems();
            } else {
                alert('Failed to update package: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            void 0;
            alert(safeErrorMessage(error));
        }
    };

    const handleStoreUpdate = (item) => {
        setStoreUpdateItem(item);

        if (item.brands && item.brands.length > 0) {
            const isLitre = isLitreItem(item);
            const isNos = isNosItem(item);

            const grouped = item.brands.reduce((acc, b) => {
                const name = b.name?.trim() || b.brand?.trim() || 'Unknown';
                if (!acc[name]) {
                    acc[name] = { brand: name, brandType: b.brand_type || '', packs: [] };
                }

                let packQty = '', otherQty = '', otherUnit = '';
                const packValue = Number(b.pack_size) || 0;

                if (isNos) {
                    packQty = '1';
                } else if (isLitre) {
                    if (packValue === 0) {
                        packQty = 'loose';
                    } else if ([0.1, 0.2, 0.25, 0.5, 1, 2].includes(packValue)) {
                        packQty = packValue.toString();
                    } else {
                        packQty = 'others';
                        if (packValue < 1) { otherQty = (packValue * 1000).toString(); otherUnit = 'ml'; }
                        else { otherQty = packValue.toString(); otherUnit = 'Litre'; }
                    }
                } else {
                    if (packValue === 0) {
                        packQty = 'loose';
                    } else if ([0.1, 0.2, 0.25, 0.5, 1, 2].includes(packValue)) {
                        packQty = packValue.toString();
                    } else {
                        packQty = 'others';
                        if (packValue < 1) { otherQty = (packValue * 1000).toString(); otherUnit = 'Grams'; }
                        else { otherQty = packValue.toString(); otherUnit = 'Kg'; }
                    }
                }

                const isInc = Number(b.gst_inc ?? b.gst_inclusive) === 1;
                const gstPct = Number(b.gst) || 0;
                const baseRate = Number(b.rate) || 0;
                // Backend stores base cost; for inclusive, reconstruct the user-facing rate (cost + gst)
                const displayRate = isInc && gstPct > 0 ? baseRate * (1 + gstPct / 100) : baseRate;
                const displayAutoRate = packValue > 0
                    ? (displayRate / packValue).toFixed(2)
                    : displayRate.toFixed(2);

                acc[name].packs.push({
                    packQty,
                    otherQty,
                    otherUnit,
                    rate: displayRate.toFixed(2),
                    autoRate: displayAutoRate,
                    gst: b.gst !== undefined && b.gst !== null ? b.gst : undefined,
                    gstType: isInc ? 'inclusive' : 'exclusive',
                });

                return acc;
            }, {});

            setBrandList(Object.values(grouped));
        } else {
            setBrandList([{ brand: '', packs: [{ packQty: '', otherQty: '', otherUnit: '', rate: '', autoRate: '' }] }]);
        }

        setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 50);
    };

    const handleStoreSave = async () => {
        try {

            const isLitre = isLitreItem(storeUpdateItem);
            const isNos = isNosItem(storeUpdateItem);
            const payload = { brand: [] };

            brandList.forEach(b => {
                b.packs.forEach(p => {
                    let packValue = 0;
                    const rateValue = Number(p.rate) || 0;
                    let perUnitRate = 0;

                    if (isNos) {
                        packValue = 1;
                        perUnitRate = rateValue;
                    } else if (isLitre) {
                        if (p.packQty === 'loose') {
                            packValue = 0;
                            perUnitRate = rateValue;
                        } else if (p.packQty === 'others') {
                            const qty = Number(p.otherQty) || 0;
                            packValue = p.otherUnit === 'ml' ? qty / 1000 : qty;
                            perUnitRate = packValue > 0 ? rateValue / packValue : 0;
                        } else {
                            packValue = Number(p.packQty);
                            perUnitRate = packValue > 0 ? rateValue / packValue : 0;
                        }
                    } else {
                        if (p.packQty === 'loose') {
                            packValue = 0;
                            perUnitRate = rateValue;
                        } else if (p.packQty === 'others') {
                            const qty = Number(p.otherQty) || 0;
                            const unit = p.otherUnit;
                            if (unit === 'Kg' || unit === 'Litre') packValue = qty;
                            else if (unit === 'Grams') packValue = qty / 1000;
                            else if (unit === 'Pcs') packValue = qty;
                            perUnitRate = packValue > 0 ? rateValue / packValue : 0;
                        } else {
                            packValue = Number(p.packQty);
                            perUnitRate = packValue > 0 ? rateValue / packValue : 0;
                        }
                    }

                    // Use user-picked GST (fallback to item.gst); applies to loose qty too
                    const gstValue = p.gst !== undefined && p.gst !== null && p.gst !== ""
                        ? Number(p.gst)
                        : (parseInt(storeUpdateItem?.gst) || 0);

                    // GST amount per unit (Excl: per_unit × gst%; Incl: extracted from per_unit)
                    const gstType = p.gstType || 'inclusive';
                    let gstUnitAmount = 0;
                    if (gstValue > 0 && perUnitRate > 0) {
                        gstUnitAmount = gstType === 'inclusive'
                            ? perUnitRate - perUnitRate / (1 + gstValue / 100)
                            : perUnitRate * (gstValue / 100);
                    }

                    // For inclusive: convert rate to base cost (excl GST); for exclusive: rate is already cost
                    const costRate = gstType === 'inclusive' && gstValue > 0
                        ? rateValue / (1 + gstValue / 100)
                        : rateValue;
                    const costPerUnit = gstType === 'inclusive' && gstValue > 0
                        ? perUnitRate / (1 + gstValue / 100)
                        : perUnitRate;

                    payload.brand.push({
                        name: b.brand,
                        brand_type: b.brandType,
                        pack_size: packValue,
                        rate: parseFloat(costRate.toFixed(2)),
                        per_unit: parseFloat(costPerUnit.toFixed(2)),
                        gst: gstValue,
                        gst_unit: parseFloat(gstUnitAmount.toFixed(2)),
                        gst_inc: gstType === 'inclusive' ? 1 : 0,
                    });
                });
            });

            const itemId = storeUpdateItem?.id;
            setLoadingRow(itemId);

            const response = await validatedFetch(`${API_BASE_URL}/item/store/brand/${itemId}`, {
                method: 'PUT',
                headers: {  'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await readJson(response);
            if (!result.error) {
                setLoadingRow(null);
                setLastUpdatedItem(itemId);
                setUpdatedItems(prev => new Set([...prev, itemId]));
                setStoreUpdateItem(null);
                await refreshAllItems();
            } else {
                alert(result.message || 'Failed to save brand details');
                setLoadingRow(null);
            }
        } catch (err) {
            void 0;
            setLoadingRow(null);
            alert(safeErrorMessage(err));
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="p-8">
            {userRole === 'rcs-store' && (
                <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    <span>
                        Please note that the prices being changed will be applicable for orders placed after the change. Orders received before the price change will be invoiced as per price at the time of order.
                    </span>
                </div>
            )}
            <h2 className="text-xl font-bold text-blue-800 mb-4">Select Category</h2>

            <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <div className="flex flex-wrap gap-3">
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => handleCategoryClick(category)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium border shadow-sm transition flex items-center gap-2 ${
                                selectedCategory === category
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-blue-700 border-blue-400 hover:bg-blue-100'
                            }`}
                        >
                            <span>{categoryIcons[category]}</span>
                            <span>{category}</span>
                        </button>
                    ))}
                </div>
                <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                />
            </div>

            <div className="flex justify-between items-center mb-4 w-full">
                <h3 className="text-lg font-semibold text-gray-800">
                    {labelMap[selectedCategory.toLowerCase()] || selectedCategory} Items
                </h3>

                {userRole === 'rcs-store' && selectedCategory === 'Veg, Meat, Dairy' && (
                    <div className="flex gap-2">
                        {activeEditField !== 'rate' ? (
                            <button
                                onClick={() => setActiveEditField('rate')}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700"
                            >
                                Rate
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => setActiveEditField(null)}
                                    className="bg-gray-500 text-white px-4 py-2 rounded-lg shadow hover:bg-gray-600"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleRateSave}
                                    className="bg-green-600 text-white px-4 py-2 rounded-lg shadow hover:bg-green-700"
                                >
                                    Save
                                </button>
                            </>
                        )}
                    </div>
                )}

                {userRole === 'rcs-store' && hasHighlightedRows && (
                    <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded-full bg-orange-400"></span>
                            <span className="text-gray-700">Last Updated</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded-full bg-green-400"></span>
                            <span className="text-gray-700">Updated</span>
                        </div>


                    </div>
                )}

                {isAdmin && !newItem && (
                    <button
                        onClick={handleAddRow}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-2 text-sm rounded-md shadow-sm transition"
                    >
                        <span className="text-base">＋</span>
                        <span>Item</span>
                    </button>
                )}
            </div>

            {successMessage && (
                <div className="mb-3 px-4 py-2 bg-green-200 text-green-900 border border-green-400 rounded text-sm font-semibold shadow-sm">
                    ✅ {successMessage}
                </div>
            )}

            <div className={`flex gap-6 ${newItem ? 'items-start' : ''}`}>
                <div className={`${newItem ? 'w-2/3' : 'w-full'}`}>
                    <table className="w-full table-auto border border-gray-300 text-sm mb-8">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                {tableFields.map((field) => (
                                    <th key={field} className="border px-4 py-2 capitalize">
                                        {field === 'rate'
                                            ? getRateColumnLabel()
                                            : field === 'expiryDate'
                                                ? 'Discount Expiry'
                                                : field === 'gst'
                                                    ? 'GST'
                                                    : field.charAt(0).toUpperCase() + field.slice(1)}
                                    </th>
                                ))}
                                {selectedCategory === 'Veg, Meat, Dairy' && activeEditField === 'rate' && (
                                    <th className="border px-4 py-2">Enter Rate</th>
                                )}
                                {userRole === 'rcs-admin' && (
                                    <th className="border px-4 py-2">Action</th>
                                )}
                           <th className="border px-4 py-2">Last Updated</th>
                                {userRole === 'rcs-store' && selectedCategory !== 'Veg, Meat, Dairy' && !storeUpdateItem && (
                                    <th className="border px-4 py-2">Action</th>
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {displayedCategoryItems.map((item, index) => (
                                <tr
                                    key={index}
                                    className={`
                                        ${lastUpdatedItem === item.id ? 'bg-orange-200' : ''}
                                        ${updatedItems.has(item.id) && lastUpdatedItem !== item.id ? 'bg-green-200' : ''}
                                    `}
                                >
                                    {tableFields.map((field) => (
                                        <td key={field} className="border px-4 py-2">
                                            {field === 'item' ? (
                                                <div className="flex items-center gap-2">
                                                    <span>{item.name}</span>
                                                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                                        {item.indent}
                                                    </span>
                                                </div>
                                            ) : field === 'package' ? (
                                                (() => {
                                                    const brandArray = Array.isArray(item.brands) ? item.brands : [];
                                                    if (brandArray.length === 0) return '-';
                                                    const uniqueQty = [...new Set(brandArray.map(b => getPackDisplayLabel(b, item)))];
                                                    return uniqueQty.join(', ');
                                                })()
                                            ) : field === 'brands' ? (
                                                <span
                                                    className="text-blue-600 underline cursor-pointer"
                                                    onClick={() =>
                                                        setBrandPopup({ open: true, brands: item.brands ?? [], itemName: item.name, item })
                                                    }
                                                >
                                                    {item.brands ? item.brands.length : 0}
                                                </span>
                                            ) : field === 'rate' ? (
                                                selectedCategory === 'Veg, Meat, Dairy' ? (
                                                    item.rate || '-'
                                                ) : (
                                                    (() => {
                                                        const brandArray = Array.isArray(item.brands) ? item.brands : [];
                                                        if (brandArray.length === 0) return '-';
                                                        const autoRates = brandArray
                                                            .map(b => {
                                                                if ((!b.pack_size || b.pack_size === 0) && b.rate) return Number(b.rate);
                                                                if (b.pack_size > 0 && b.rate) return Number(b.rate) / Number(b.pack_size);
                                                                return null;
                                                            })
                                                            .filter(v => v !== null && !isNaN(v));
                                                        if (autoRates.length === 0) return '-';
                                                        const min = Math.min(...autoRates).toFixed(2);
                                                        const max = Math.max(...autoRates).toFixed(2);
                                                        return min === max ? `${min}` : `${min} - ${max}`;
                                                    })()
                                                )
                                            ) : (
                                                item[field]
                                            )}
                                        </td>
                                    ))}

                                    {selectedCategory === 'Veg, Meat, Dairy' && activeEditField === 'rate' && (
                                        <td className="border px-4 py-2">
                                            <input
                                                type="number"
                                                className="border px-2 py-1 rounded w-24 text-center"
                                                value={pendingEdits[item.id] || ''}
                                                onChange={(e) =>
                                                    setPendingEdits(prev => ({ ...prev, [item.id]: e.target.value }))
                                                }
                                            />
                                        </td>
                                    )}

                                    {userRole !== 'rcs-store' && (
                                        <td className="border px-2 py-1">
                                            <button
                                                onClick={() => {
                                                    setEditIndex(index);
                                                    setEditedItem({
                                                        id: item.id,
                                                        item: item.name,
                                                        unit: item.indent,
                                                        gst: item.gst,
                                                        discount: item.discount,
                                                        rate: item.rate,
                                                        expiryDate: item.expiryDate,
                                                        category: normalizeCategory(item.category),
                                                    });
                                                    setNewItem(null);
                                                    setTimeout(() => {
                                                        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }, 0);
                                                }}
                                                className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    )}
<td className="border px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                                        {lastUpdatedMap[String(item.id)]
                                            ? new Date(lastUpdatedMap[String(item.id)]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : '-'}
                                    </td>
                                    {userRole === 'rcs-store' && selectedCategory !== 'Veg, Meat, Dairy' && !storeUpdateItem && (
                                        <td className="border px-2 py-2 flex items-center gap-2">
                                            {loadingRow === item.id ? (
                                                <>
                                                    <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path>
                                                    </svg>
                                                    <span className="text-blue-700 text-xs">Updating...</span>
                                                </>
                                            ) : (
                                                <button
                                                    className="p-1 text-blue-600 rounded transition"
                                                    onClick={() => handleStoreUpdate(item)}
                                                    title="Edit / Update"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                        <line x1="15" y1="12" x2="15" y2="18"></line>
                                                        <line x1="12" y1="15" x2="18" y2="15"></line>
                                                    </svg>
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {activeEditField === 'rate' && selectedCategory === 'Veg, Meat, Dairy' && (
                        <div className="mt-4 text-right">
                            <button
                                onClick={handleRateSave}
                                className="bg-green-600 text-white px-5 py-2 rounded-lg shadow hover:bg-green-700"
                            >
                                Save Rate
                            </button>
                        </div>
                    )}
                </div>

                {storeUpdateItem && (
                    <StoreUpdateForm
                        item={storeUpdateItem}
                        brandList={brandList}
                        setBrandList={setBrandList}
                        onCancel={() => setStoreUpdateItem(null)}
                        onSave={handleStoreSave}
                    />
                )}

                {(newItem || editedItem) && (
                    <div
                        ref={formRef}
                        className="w-1/3 bg-white p-4 border border-gray-300 rounded-xl shadow relative max-h-[450px] overflow-y-auto"
                    >
                        <button
                            onClick={() => { setNewItem(null); setEditedItem(null); setEditIndex(null); }}
                            className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-lg font-bold"
                            title="Close"
                        >
                            ✖
                        </button>
                        <h4 className="text-md font-bold text-gray-700 mb-4">
                            {editedItem ? 'Edit Item' : 'Add New Item'}
                        </h4>
                        <div className="space-y-4 text-sm">
                            {formFields.includes('category') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Category</label>
                                    <select
                                        value={editedItem ? editedItem.category : newItem.category}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, category: e.target.value })
                                                : setNewItem({ ...newItem, category: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                    >
                                        <option value="food">Grocery</option>
                                        <option value="dailie">Veg / Meat / Dairy</option>
                                        <option value="housekeeping">Housekeeping</option>
                                        <option value="stationery">Stationery</option>
                                        <option value="cosmetics">Cosmetics</option>
                                        <option value="others">Others</option>
                                    </select>
                                </div>
                            )}
                            {formFields.includes('item') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Item</label>
                                    <input
                                        type="text"
                                        value={editedItem ? editedItem.item : newItem.item}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, item: e.target.value })
                                                : setNewItem({ ...newItem, item: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                    />
                                </div>
                            )}
                            {formFields.includes('unit') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Unit</label>
                                    <select
                                        value={editedItem ? editedItem.unit : newItem.unit}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, unit: e.target.value })
                                                : setNewItem({ ...newItem, unit: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                    >
                                        <option value="">Select Unit</option>
                                        {units.map((unit, i) => (
                                            <option key={i} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {formFields.includes('rate') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Rate (Per Kg)</label>
                                    <input
                                        type="number"
                                        value={editedItem ? editedItem.rate : newItem.rate}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, rate: e.target.value })
                                                : setNewItem({ ...newItem, rate: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                        placeholder="Enter Rate"
                                    />
                                </div>
                            )}
                            {formFields.includes('gst') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">GST (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={editedItem ? editedItem.gst : newItem.gst}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, gst: e.target.value })
                                                : setNewItem({ ...newItem, gst: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                        placeholder="Enter GST %"
                                    />
                                </div>
                            )}
                            {formFields.includes('discount') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Discount (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={editedItem ? editedItem.discount : newItem.discount}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, discount: e.target.value })
                                                : setNewItem({ ...newItem, discount: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                        placeholder="Enter Discount %"
                                    />
                                </div>
                            )}
                            {formFields.includes('expiryDate') && (
                                <div>
                                    <label className="block font-medium text-gray-600 mb-1">Discount Expiry Date</label>
                                    <input
                                        type="date"
                                        value={editedItem ? editedItem.expiryDate || '' : newItem.expiryDate || ''}
                                        onChange={(e) =>
                                            editedItem
                                                ? setEditedItem({ ...editedItem, expiryDate: e.target.value })
                                                : setNewItem({ ...newItem, expiryDate: e.target.value })
                                        }
                                        className="w-full border px-3 py-2 rounded"
                                    />
                                </div>
                            )}
                            <div className="flex justify-between items-center gap-4 mt-4">
                                <button
                                    onClick={() => { if (editedItem) handleUpdateItem(); else handleSaveRow(); }}
                                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                                >
                                    {editedItem ? 'Save Changes' : 'Add Item'}
                                </button>
                                {editedItem && (
                                    <button
                                        onClick={handleDeleteItem}
                                        className="text-red-600 hover:text-red-800 p-2 rounded"
                                        title="Delete Item"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Brand Popup */}
            {brandPopup.open && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl relative overflow-hidden">
                    <div className="bg-blue-600 text-white text-lg font-semibold px-4 py-3 w-full flex justify-between items-center">
    <span>Brand Details</span>
    <button
        onClick={() => setBrandPopup({ open: false, brands: [], itemName: '', item: null })}
        className="text-white hover:text-red-200 text-xl font-bold"
    >✕</button>
</div>
                        <div className="px-6 pt-4 pb-2 text-gray-900 text-base font-semibold border-b border-gray-300">
                            {brandPopup.itemName}
                        </div>
                        <div className="p-6">
                            <table className="w-full table-auto border border-gray-300 text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border px-4 py-2 text-left">Brand</th>
                                        <th className="border px-4 py-2 text-left">Pack Rate</th>
                                        <th className="border px-4 py-2 text-left">Pack (Qty)</th>
                                        <th className="border px-4 py-2 text-left">
                                            {brandPopup.item && isLitreItem(brandPopup.item)
                                                ? 'Rate per Ltr'
                                                : brandPopup.item && isNosItem(brandPopup.item)
                                                    ? 'Rate per Pc'
                                                    : 'Rate per Kg'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const brandArray = Array.isArray(brandPopup.brands)
                                            ? brandPopup.brands
                                            : brandPopup.brands ? [brandPopup.brands] : [];

                                        if (brandArray.length === 0) {
                                            return (
                                                <tr>
                                                    <td className="border px-4 py-3 text-center" colSpan="4">
                                                        No brands available.
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return brandArray.map((b, i) => (
                                            <tr key={i}>
                                                <td className="border px-4 py-2">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span>{b.name || '-'}</span>
                                                        {b.brand_type && (
                                                            <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                                                                b.brand_type === 'JPC' ? 'bg-blue-100 text-blue-700' :
                                                                b.brand_type === 'Cooperative' ? 'bg-green-100 text-green-700' :
                                                                'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                {b.brand_type === 'Non-JPC' ? 'Branded' : b.brand_type}
                                                            </span>
                                                        )}
                                                        {Number(b.pack_size) > 0 && Number(b.gst) > 0 && (
                                                            <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-amber-100 text-amber-700">
                                                                {Number(b.gst)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="border px-4 py-2 text-right whitespace-nowrap">
                                                    {(() => {
                                                        const baseRate = Number(b.rate) || 0;
                                                        const gst = Number(b.gst) || 0;
                                                        const packSize = Number(b.pack_size) || 0;
                                                        const gstAmt = (packSize > 0 && gst > 0) ? baseRate * (gst / 100) : 0;
                                                        return (
                                                            <>
                                                                <span>₹ {baseRate.toFixed(2)}</span>
                                                                {gstAmt > 0 && (
                                                                    <span className="text-amber-700"> + ₹ {gstAmt.toFixed(2)}</span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="border px-4 py-2">
                                                    {getPackDisplayLabel(b, brandPopup.item || {})}
                                                </td>
                                                <td className="border px-4 py-2 text-right font-semibold whitespace-nowrap">
                                                    {(() => {
                                                        const baseRate = Number(b.rate) || 0;
                                                        const packSize = Number(b.pack_size) || 0;
                                                        const gst = Number(b.gst) || 0;
                                                        const perUnit = packSize === 0 ? baseRate : (baseRate / packSize);
                                                        const gstPerUnit = (packSize > 0 && gst > 0) ? perUnit * (gst / 100) : 0;
                                                        return (
                                                            <>
                                                                <span>₹ {perUnit.toFixed(2)}</span>
                                                                {gstPerUnit > 0 && (
                                                                    <span className="text-amber-700 font-normal"> + ₹ {gstPerUnit.toFixed(2)}</span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={() => setBrandPopup({ open: false, brands: [], itemName: '', item: null })}
                                    className="bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Items;
